/**
 * SuccessWare BookIt Service
 * 
 * Handles pushing appointments from the call center to SuccessWare
 * using the BookIt API with company-specific field mappings.
 */

import { supabase } from '../supabase';
import type { ServiceNoteFields } from '../config/successware-field-mappings';
import { transformServiceNotesToBookItInput, getCompanyMapping } from '../config/successware-field-mappings';

export interface CustomerInfo {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface PushBookingRequest {
  companyId: string;
  customerInfo: CustomerInfo;
  serviceNotes: ServiceNoteFields;
  callSid?: string;
}

export interface PushBookingResult {
  success: boolean;
  message: string;
  jobId?: string;
  jobNumber?: string;
  serviceAccountId?: string;
  error?: string;
}

/**
 * SuccessWare BookIt Service Class
 */
export class SuccessWareBookItService {
  /**
   * Push booking to SuccessWare BookIt API
   */
  async pushBooking(request: PushBookingRequest): Promise<PushBookingResult> {
    console.log(`[BookItService] Pushing booking for company: ${request.companyId}`);
    
    try {
      // 1. Get company configuration
      const { data: companyConfig, error: configError } = await supabase
        .from('company_configs')
        .select('*')
        .eq('slug', request.companyId)
        .single();
      
      if (configError || !companyConfig) {
        return {
          success: false,
          message: `Company configuration not found for ${request.companyId}`,
          error: configError?.message,
        };
      }
      
      // 2. Get field mapping for this company
      const mapping = getCompanyMapping(request.companyId);
      
      if (!mapping) {
        return {
          success: false,
          message: `No field mapping configured for ${request.companyId}`,
        };
      }
      
      // 3. Get SuccessWare credentials
      const { data: credentials, error: credError } = await supabase
        .from('integration_credentials')
        .select('*')
        .eq('provider_id', (await this.getSuccessWareProviderId()))
        .eq('company_id', companyConfig.id)
        .single();
      
      if (credError || !credentials) {
        return {
          success: false,
          message: 'SuccessWare credentials not found',
          error: credError?.message,
        };
      }
      
      // 4. Call edge function to push to SuccessWare
      const { data, error } = await supabase.functions.invoke('successware-push-booking', {
        body: {
          companyId: request.companyId,
          tenantId: companyConfig.tenant_id || mapping.tenantId,
          credentials: {
            username: credentials.username,
            password: credentials.password,
            tenantId: companyConfig.tenant_id || mapping.tenantId,
          },
          customerInfo: request.customerInfo,
          serviceNotes: request.serviceNotes,
          callSid: request.callSid,
        },
      });
      
      if (error) {
        console.error('[BookItService] Push failed:', error);
        return {
          success: false,
          message: 'Failed to push booking to SuccessWare',
          error: error.message,
        };
      }
      
      // 5. Log the push to database
      if (data.success && request.callSid) {
        await this.logBookingPush({
          callSid: request.callSid,
          companyId: companyConfig.id,
          jobId: data.jobId,
          jobNumber: data.jobNumber,
          serviceAccountId: data.serviceAccountId,
        });
      }
      
      return data;
    } catch (error: any) {
      console.error('[BookItService] Unexpected error:', error);
      return {
        success: false,
        message: 'Unexpected error occurred',
        error: error.message,
      };
    }
  }
  
  /**
   * Get SuccessWare provider ID
   */
  private async getSuccessWareProviderId(): Promise<string> {
    const { data } = await supabase
      .from('integration_providers')
      .select('id')
      .eq('slug', 'successware')
      .single();
    
    return data?.id || '';
  }
  
  /**
   * Log booking push to database
   */
  private async logBookingPush(params: {
    callSid: string;
    companyId: string;
    jobId?: string;
    jobNumber?: string;
    serviceAccountId?: string;
  }) {
    try {
      await supabase.from('successware_booking_pushes').insert({
        call_sid: params.callSid,
        company_id: params.companyId,
        sw_job_id: params.jobId,
        sw_job_number: params.jobNumber,
        sw_service_account_id: params.serviceAccountId,
        pushed_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error('[BookItService] Failed to log booking push:', error);
      // Non-fatal, continue
    }
  }
  
  /**
   * Validate booking request before pushing
   */
  validateRequest(request: PushBookingRequest): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    // Validate company
    if (!request.companyId) {
      errors.push('Company ID is required');
    }
    
    // Validate customer info
    if (!request.customerInfo.firstName) errors.push('Customer first name is required');
    if (!request.customerInfo.lastName) errors.push('Customer last name is required');
    if (!request.customerInfo.phoneNumber) errors.push('Customer phone number is required');
    if (!request.customerInfo.address1) errors.push('Customer address is required');
    if (!request.customerInfo.city) errors.push('Customer city is required');
    if (!request.customerInfo.state) errors.push('Customer state is required');
    if (!request.customerInfo.zipCode) errors.push('Customer zip code is required');
    
    // Validate service notes (at least one field should be filled)
    const hasServiceNotes = Object.values(request.serviceNotes).some(v => v && v.trim() !== '');
    if (!hasServiceNotes) {
      errors.push('At least one service note field must be filled');
    }
    
    // Check if field mapping exists
    const mapping = getCompanyMapping(request.companyId);
    if (!mapping) {
      errors.push(`No field mapping configured for company: ${request.companyId}`);
    }
    
    return {
      valid: errors.length === 0,
      errors,
    };
  }
  
  /**
   * Preview how service notes will be formatted for SuccessWare
   */
  previewBookingNotes(companyId: string, serviceNotes: ServiceNoteFields): string | null {
    const mapping = getCompanyMapping(companyId);
    
    if (!mapping) {
      return null;
    }
    
    return mapping.fieldMappers.workRequestNotes(serviceNotes);
  }
}

// Export singleton instance
export const successWareBookItService = new SuccessWareBookItService();
